import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { useRefetchTransactions } from "../api/services"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import FloatingActionButton from "../components/FloatingActionButton"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseInputSearch from "../../../components/elements/BaseInputSearch"
import TransactionTableRow from "../components/banking/TransactionTableRow"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { transactionElementType } from "../types"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { ReactComponent as REFRESH } from "../../../assets/RefreshICON.svg"
import { useIgnoreTransactionMutation } from "../api/services"
import AssignTransactionModal from "../components/banking/AssignTransactionModal"

export default function TransactionsPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.TRANSACTION_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.TRANSACTION_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.TRANSACTION_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm, LocalStorageKeys.TRANSACTION_FILTER + "searchTerm")
    useResetPageOnFilterChange([searchParams], () => handlePageChange(1))
    const queryKey = GeneralQueryKeys.TRANSACTIONS_ALL + page + searchParams
    const { data, isLoading, error } = useQuery({
        queryKey: [queryKey],
        queryFn: () => getPaginatedData({ pageType: "banking", page: page, search: searchParams, limit: 15, status: "UNASSIGNED" }),
    })
    const displayedTransactions: transactionElementType[] = useMemo(() => data?.docs, [data?.docs])

    const queryClient = useQueryClient()
    const { mutate: refreshTransactions } = useRefetchTransactions(queryClient, queryKey)

    const [transaction, settransaction] = useState<transactionElementType>()
    const modalCon = useContext(ModalContext)

    // IGNORE TRANSACTION
    const { mutate: ignoreTransaction } = useIgnoreTransactionMutation(queryClient, queryKey)
    const handleIgnoreTransaction = (tid: string) => {
        ignoreTransaction(tid)
    }

    // FE LOGIC
    const paymentTableColumns: ColumnType[] = [
        {
            label: "Buchungsdatum",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Status",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Betrag",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Absender",
        },
        {
            label: "Verwendungszweck",
            width: " sm:min-w-[8rem] w-fit",
        },
        {
            label: "",
        }
    ]

    return (
        <BasePage>
            <HeadBanner title={"Nicht zugeordnete Transaktionen"} 
            button={<REFRESH className="w-8 h-8" onClick={() => refreshTransactions()}/>}
            />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <BaseButtonRow>
                <BaseInputSearch placeholder="Nach Betrag / Absender / Verwendungszweck suchen..." searchTerm={searchTerm} setsearchTerm={setsearchTerm} classProps="!w-64! sm:!w-96 text-sm"/>
            </BaseButtonRow>
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps="overflow-x-hidden my-4">
                        <BaseTable
                            columns={paymentTableColumns}
                            tbody={displayedTransactions?.map((item: transactionElementType) => (
                                <TransactionTableRow
                                    transaction={item}
                                    openPaymentModal={() => {
                                        settransaction(item)
                                        modalCon?.trigger(0)   
                                    }}
                                    ignoreTransaction={() => handleIgnoreTransaction(item._id)}
                                />
                            ))}
                        />

                        {(!displayedTransactions || displayedTransactions.length === 0) && (
                            <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erwarteten Zahlungen</p>
                        )}
                    </BaseGrowBody>
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4 z-0">
                        <Pagination
                            defaultCurrent={1}
                            current={page}
                            pageSize={15}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}
            <AssignTransactionModal modalId={0} transaction={transaction} transactionQueryKey={queryKey} closeModal={() => modalCon?.trigger(0)} />
            <FloatingActionButton currentPage="transaction" />
        </BasePage>
    )
}
